import { Component, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { MainMenuElement } from '@base/classes/BaseClasses';
import { Utils } from '@core/helpers/Utils';
import { ServiceBagService } from '@m4u-angular/base/component/ServiceBag';
import { AppConfigService, VersionDescriptor } from '@m4u-angular/configuration/app.config.service';
import { Subscription } from 'rxjs';
import { RouteHistoryService } from '@m4u-angular/core/framework/route-history.service';
import { BaseEntity, BaseState } from '@m4u-angular/base/entities/BaseEntities';
import { BasePageComponent } from '@m4u-angular/base/component/BasePageComponent';
import { Router } from '@angular/router';
import { M4URouteRandomizers } from '../../../../core/framework/navigator.service';

class MenuTooltip {
    Target: Element;
    Content: string;
    Visible: boolean;
}

class State extends BaseState {
    MenuItems: MainMenuElement[] = [];
    HomeMenu: MainMenuElement;
}

@Component({
    selector: 'm4u-menu-button-list',
    templateUrl: './menu-button-list.component.html',
    styleUrls: ['./menu-button-list.component.scss']
})
export class MenuButtonListComponent extends BasePageComponent<BaseEntity, State> {

    private _subscriptions: Subscription[] = [];
    private IsMultiLanguage: boolean;
    private currentPath: string;

    constructor(A: ServiceBagService,
        private routeHistoryService: RouteHistoryService,
        private appConfigService: AppConfigService) {
        super(A, 'MenuButtonListComponent', BaseEntity, State);
    }

    override OnInitComponent() {
        this.currentPath = this.A.Navigator.GetCurrentRoute();

        // Fix the path for deployment - it should start with '/'
        if (!this.currentPath.startsWith('/')) {
            this.currentPath = '/' + this.currentPath;
        }

        // Remove randomizer prefix from path
        M4URouteRandomizers.forEach(x => {
            this.currentPath = this.currentPath.replace('/'+x, '/');
        });

        this.IsMultiLanguage = this.appConfigService.M4UConfig.isMultiLanguage;
        this.InitMenuItems();
    }


    protected override OnInitTitle(): string {
        var menuTitle = this.GetDefaultTitle();
        if (this.State.HomeMenu) {
            const menuWithoutModule = this.State.HomeMenu.menu.substring(this.State.HomeMenu.menu.indexOf(".") + 1, this.State.HomeMenu.menu.length);
            menuTitle = this.A.ResourcesService.RC(`$$Pages.${menuWithoutModule}`, this.State.HomeMenu.moduleName);
        }
        return menuTitle;
    }

    InitMenuItems() {
        this._subscriptions.push(this.A.AppConfigService.AppConfig.OnGetMenu(this.A.Session.UserInfoDict)
            .subscribe(items => {
                // Filter out the menu item that matches the current path
                this.State.MenuItems = items
                    .filter(menu => menu.path !== this.currentPath);
                this.InitializeItems();

                var titleMenu = items.find(menu => menu.path === this.currentPath);
                if (titleMenu) {
                    this.State.HomeMenu = titleMenu;
                }

                // Update title
                this.RepaintToolbar();
            })
        );
    }

    InitializeItems() {
        this.State.MenuItems.forEach(menu => this.InitializeItem(menu));
    }

    InitializeItem(menu: MainMenuElement) {
        menu.text = this.GetMenuTranslation(menu);
        this.InitMenuItem(menu);

        if (menu.items) menu.items.forEach(x => this.InitializeItem(x));
    }

    GetMenuTranslation(menu: MainMenuElement) {
        const menuWithoutModule = menu.menu.substring(menu.menu.indexOf(".") + 1, menu.menu.length);
        return menu.text || this.A.ResourcesService.RC(`$$Pages.${menuWithoutModule}`, menu.moduleName);
    }

    InitMenuItem(menu: MainMenuElement) {
        const menuUserInfo = menu.moduleName && this.A.Session.UserInfoDict[menu.moduleName];
        const permissionResult = Utils.IsEmpty(menu.permission)
            ? true
            : menuUserInfo && menuUserInfo.hasOwnProperty(menu.permission) && menuUserInfo[menu.permission];
        const permissionFunctionResult = Utils.IsEmpty(menu.permissionFunction)
            ? true
            : menu.permissionFunction(menuUserInfo);
        menu.visible = !!permissionResult && permissionFunctionResult;

        // add 'rtl' class for custom icons in RTL
        if (this.A.Screen.IsRTL() && menu.icon && menu.icon.startsWith('m4u-ico')) menu.icon = `${menu.icon} rtl`;

        // set common icon for folders if it is empty
        if (menu.items && Utils.IsEmpty(menu.icon)) menu.icon = 'mdi mdi-folder-open';

        // hide main menu elements for mobile
        if (this.A.Screen.IsMobile()) menu.visible = menu.visible && (menu.isVisibleOnMobile == undefined ? true : menu.isVisibleOnMobile);
    }

    OnButtonClick(menu: MainMenuElement) {
        if (menu.click && typeof menu.click === 'function') {
            menu.click();
            return;
        }

        if (!menu.path) return;

        const menuUserInfo = menu.moduleName ? this.A.Session.UserInfoDict[menu.moduleName] : this.A.Session.UserInfos[0];
        const pathParams = menu.pathParams && menu.pathParams(menuUserInfo) || [];

        this.routeHistoryService.ClearHistory();
        this.A.Navigator.Navigate([menu.path, pathParams], menu.queryParams || null);
    }

    override OnDestroyComponent() {
        this._subscriptions.forEach(x => x.unsubscribe());
    }
}
